import React, { forwardRef, type ComponentType, type FC } from 'react';
import styled from 'styled-components';
import SvgIcon from '@mui/material/SvgIcon';
import type { SxProps, Theme } from '@mui/material/styles';

interface SvgContentProps {
  stroke?: string;
  strokeWidth?: number;
}

interface IconProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'color'
> {
  color?: string;
  colorHover?: string;
  icon?: ComponentType<{ sx?: SxProps<Theme> }>;
  size?: string | number;
  svgContent?: FC<SvgContentProps>;
  svgStrokeWidth?: number;
  sx?: SxProps<Theme>;
  viewBox?: string;
}

const IconWrapper = styled.div`
  display: inline-block;
  line-height: 0;
`;

const Icon = forwardRef<HTMLDivElement, IconProps>(
  (
    {
      color,
      colorHover,
      icon: IconComponent,
      size,
      svgContent: SvgContent,
      svgStrokeWidth = 0,
      sx: sxProps,
      viewBox,
      ...props
    },
    ref
  ) => {
    if (!IconComponent && !SvgContent) {
      console.error(
        'Icon component requires either `icon` or `svgContent` prop to be provided'
      );
      return null;
    }

    const sx: SxProps<Theme> = {
      color,
      fontSize: size,
      '&:hover': {
        color: colorHover,
      },
    };

    const combinedSx: SxProps<Theme> = sxProps
      ? Array.isArray(sxProps)
        ? [...sxProps, sx]
        : [sxProps, sx]
      : sx;

    return (
      <IconWrapper ref={ref} {...props}>
        {IconComponent ? (
          <IconComponent sx={combinedSx} />
        ) : SvgContent ? (
          <SvgIcon sx={combinedSx} viewBox={viewBox}>
            <SvgContent stroke={color} strokeWidth={svgStrokeWidth} />
          </SvgIcon>
        ) : null}
      </IconWrapper>
    );
  }
);

Icon.displayName = 'Icon';

export default Icon;
export type { IconProps };
